import { RowChevron } from '@metro/station-details'
import type React from 'react'
import { useTranslation } from 'react-i18next'
import type { LineView, LineViewRow } from './buildLineView'
import { LineRail } from './LineRail'
import styles from './LineStationList.module.css'

export interface LineStationListProps {
  readonly view: LineView
  /** Station / loop-close row tapped — opens that station. */
  readonly onStationTap: (stationId: string) => void
}

/** Row heights (px) — the rail SVG is drawn to exactly this height. */
function rowHeight(row: LineViewRow): number {
  if (row.kind === 'caption') return 32
  if (row.kind === 'loopClose') return 36
  if (row.transfers.length > 0) return 52
  return row.terminus ? 48 : 36
}

/**
 * The line view's station list — a rail diagram (Figma `9250:17232`):
 * "Stations · N", then one row per station with its slice of the rails,
 * grey branch captions, and the muted row a loop closes on. Every station
 * row opens the station (legend B·1 / D·1).
 */
export function LineStationList({ view, onStationTap }: LineStationListProps): React.ReactElement {
  const { t } = useTranslation('line-details')
  const caption = (row: Extract<LineViewRow, { kind: 'caption' }>): string => {
    if (row.caption.kind === 'loop') return t('caption_loop')
    const name = row.names.join(' & ')
    return row.caption.kind === 'branch'
      ? t('caption_branch', { name })
      : t('caption_via', { name })
  }
  return (
    <section className={styles.section} data-testid="line-station-list">
      <h2 className={styles.header}>{t('stations_header', { count: view.stationCount })}</h2>
      <ol className={styles.list}>
        {view.rows.map((row, index) => {
          const height = rowHeight(row)
          const rail = (
            <LineRail
              cells={row.cells}
              curves={row.kind === 'station' ? row.curves : []}
              height={height}
              color={view.color}
              outlined={view.outlined}
            />
          )
          if (row.kind === 'caption') {
            return (
              <li key={`caption-${index}`} className={styles.item} style={{ height }}>
                {rail}
                <span className={styles.caption}>{caption(row)}</span>
              </li>
            )
          }
          return (
            <li key={`${row.kind}-${row.slug}`} className={styles.item} style={{ height }}>
              <button
                type="button"
                className={styles.station}
                data-testid={row.kind === 'station' ? 'line-station-row' : 'line-loop-close-row'}
                onClick={() => onStationTap(row.stationId)}
              >
                {rail}
                <span className={styles.titles}>
                  <span
                    className={
                      row.kind === 'loopClose'
                        ? `${styles.name} ${styles.muted}`
                        : row.terminus
                          ? `${styles.name} ${styles.terminus}`
                          : styles.name
                    }
                  >
                    {row.kind === 'loopClose' ? `${row.name} ↺` : row.name}
                  </span>
                  {row.kind === 'station' && row.transfers.length > 0 && (
                    <span className={styles.transfers}>{row.transfers.join(' · ')}</span>
                  )}
                </span>
                <RowChevron />
              </button>
            </li>
          )
        })}
      </ol>
    </section>
  )
}
